import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { clsx } from 'clsx';
import { toast } from 'sonner';
import { WrapperWidget } from '@agentselly/ui';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFormRegistration } from '~/context/PropertyEditContext';
import { useAutosaveForm } from '~/hooks/useAutosaveForm';
import type {
  PropertyAddressType,
  PropertyFormType,
} from '@agentselly/immodossier-shared';
import {
  ActionSchema,
  AppAction,
  ConditionEnums,
  QualityEnums,
} from '@agentselly/immodossier-shared';
import { Bath } from '~/generated/svg/Bath';
import { Floor } from '~/generated/svg/Floor';
import { Kitchen } from '~/generated/svg/Kitchen';
import { Masonry } from '~/generated/svg/Masonry';
import { Window } from '~/generated/svg/Window';
import { useV3Redesign } from '~/helper/useV3Redesign';
import { getEnumValues } from '~/shared/getEnumValues';

interface CollapsibleSectionProps {
  readonly children: React.ReactNode;
  readonly defaultOpen?: boolean;
}

function CollapsibleSection({
  children,
  defaultOpen = true,
}: CollapsibleSectionProps) {
  const [
    isOpen,
    setIsOpen,
  ] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-end p-3 hover:bg-gray-50 dark:hover:bg-zinc-800 dark:bg-zinc-800/50 dark:hover:bg-zinc-800/50 transition-colors"
        aria-label={isOpen ? 'Collapse section' : 'Expand section'}
      >
        <svg
          className={clsx(
            'w-5 h-5 text-gray-400 dark:text-zinc-500 dark:text-zinc-400 transition-transform',
            {
              'rotate-180': isOpen,
            },
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && <div className="p-3 pt-0">{children}</div>}
    </div>
  );
}

interface SaveStatusIndicatorProps {
  readonly status: 'idle' | 'saving' | 'saved' | 'error' | 'auth_error';
  readonly t: (key: string) => string;
}

function SaveStatusIndicator({
  status,
  t,
  onRetry,
}: SaveStatusIndicatorProps & { readonly onRetry?: () => void }) {
  if (status === 'idle') {
    return (
      <div
        className="flex items-center gap-1.5 text-sm text-base-content/40"
        data-testid="autosave-ready"
      >
        <span className="w-2 h-2 rounded-full bg-base-content/20"></span>
        <span>{t('autosave_ready')}</span>
      </div>
    );
  }

  if (status === 'saving') {
    return (
      <div
        className="flex items-center gap-1.5 text-sm text-base-content/60"
        data-testid="autosave-saving"
      >
        <span className="loading loading-spinner loading-xs"></span>
        <span>{t('autosave_saving')}</span>
      </div>
    );
  }

  if (status === 'saved') {
    return (
      <div
        className="flex items-center gap-1.5 text-sm text-success"
        data-testid="autosave-saved"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
        <span>{t('autosave_saved')}</span>
      </div>
    );
  }

  if (status === 'error' || status === 'auth_error') {
    return (
      <div
        className="flex items-center gap-2 text-sm text-error"
        data-testid="autosave-error"
      >
        <svg
          className="w-4 h-4 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>{t('autosave_error')}</span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="ml-1 px-2 py-0.5 rounded bg-error text-white text-xs hover:bg-error/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error/50"
            data-testid="autosave-retry"
          >
            {t('autosave_retry')}
          </button>
        )}
      </div>
    );
  }

  return null;
}

interface Props {
  readonly dossierId: string;
  readonly property: PropertyFormType;
  readonly address: PropertyAddressType;
  readonly propertyConditions?: Pick<
    PropertyFormType,
    | 'condition_masonry'
    | 'condition_bathrooms'
    | 'condition_flooring'
    | 'condition_kitchen'
    | 'condition_windows'
  >;
  readonly propertyQualities?: Pick<
    PropertyFormType,
    | 'quality_masonry'
    | 'quality_bathrooms'
    | 'quality_flooring'
    | 'quality_kitchen'
    | 'quality_windows'
  >;
}

const iconMap: { [key: string]: React.ReactNode } = {
  condition_bathrooms: <Bath />,
  condition_flooring: <Floor />,
  condition_kitchen: <Kitchen />,
  condition_masonry: <Masonry />,
  condition_windows: <Window />,
  quality_bathrooms: <Bath />,
  quality_flooring: <Floor />,
  quality_kitchen: <Kitchen />,
  quality_masonry: <Masonry />,
  quality_windows: <Window />,
};

function LegacyPropertyConditionsQualityFormWidget(props: Props) {
  const { t } = useTranslate();

  const combinedDefaults = {
    ...props.property,
    ...props.address,
  };
  const defaultValues = Object.fromEntries(
    Object.entries(combinedDefaults).map(
      ([
        key,
        value,
      ]) => [
        key,
        value ?? '',
      ],
    ),
  );

  const form = useForm<PropertyFormType>({
    defaultValues: defaultValues,
    resolver: zodResolver(ActionSchema[AppAction.PropertyForm] as any),
  });

  const { saveStatus, triggerManualSave, retrySave } = useAutosaveForm({
    form,
    dossierId: props.dossierId,
    propertyId: (props.property as any)?.id || (props.property as any)?._id,
    action: AppAction.PropertyForm,
    debounceMs: 3000,
    showToastOnFirstSave: true,
    onSaveError: () => {
      toast.error(t('property_form_not_saved') || 'Speichern fehlgeschlagen');
    },
  });

  useFormRegistration(triggerManualSave);

  const propertyConditions = getEnumValues(ConditionEnums as any);
  const propertyQualities = getEnumValues(QualityEnums as any);

  const isSaving = saveStatus === 'saving';

  const renderRadioGroup = (
    name: keyof PropertyFormType,
    options: string[],
  ) => {
    const { field } = useController({
      name,
      control: form.control,
    });
    const selectedValue = field.value;

    return (
      <div className="form-control mb-6">
        <div className="flex items-center gap-3 mb-4">
          {iconMap[name as string] && (
            <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-primary">
              {iconMap[name as string]}
            </span>
          )}
          <span className="font-semibold text-lg">
            {t(`property_form_${String(name)}_name`)}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {options.map((option, idx) => {
            const isSelected = selectedValue === option;
            return (
              <label
                key={idx}
                className={clsx(
                  'relative flex flex-col items-center justify-center gap-1 p-2 rounded-lg cursor-pointer transition-all duration-200 ease-in-out border-2',
                  'hover:shadow-md dark:hover:shadow-gray-900/50 hover:-translate-y-0.5',
                  isSelected
                    ? 'bg-primary text-white shadow-sm dark:shadow-gray-900/50 border-primary'
                    : 'bg-base-100 border-base-300 hover:border-primary/50 hover:bg-base-200/70 shadow-sm dark:shadow-gray-900/50',
                )}
              >
                <input
                  type="radio"
                  name={field.name}
                  value={option}
                  checked={isSelected}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  className="sr-only"
                />
                <span
                  className={clsx(
                    'text-xs font-semibold text-center leading-tight',
                    isSelected ? 'text-white' : 'text-base-content/80',
                  )}
                >
                  {t('property_form_value_' + option)}
                </span>
                {isSelected && (
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-white dark:bg-zinc-800" />
                )}
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <WrapperWidget>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold">
            {props.propertyConditions
              ? t('property_form_condition_title')
              : t('property_form_quality_title')}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <SaveStatusIndicator
            status={saveStatus}
            t={t}
            onRetry={retrySave}
          />
        </div>
      </div>

      <form
        className={clsx('flex flex-col gap-3 w-full', {
          'opacity-80 animate-pulse': isSaving,
        })}
      >
        {/* Conditions */}
        {props.propertyConditions && (
          <CollapsibleSection>
            <div className="pt-3 space-y-4">
              {renderRadioGroup('condition_bathrooms', propertyConditions)}
              {renderRadioGroup('condition_flooring', propertyConditions)}
              {renderRadioGroup('condition_kitchen', propertyConditions)}
              {renderRadioGroup('condition_masonry', propertyConditions)}
              {renderRadioGroup('condition_windows', propertyConditions)}
            </div>
          </CollapsibleSection>
        )}

        {/* Quality */}
        {props.propertyQualities && (
          <CollapsibleSection>
            <div className="pt-3 space-y-4">
              {renderRadioGroup('quality_bathrooms', propertyQualities)}
              {renderRadioGroup('quality_flooring', propertyQualities)}
              {renderRadioGroup('quality_kitchen', propertyQualities)}
              {renderRadioGroup('quality_masonry', propertyQualities)}
              {renderRadioGroup('quality_windows', propertyQualities)}
            </div>
          </CollapsibleSection>
        )}

        {/* Manueller Speichern-Button als Fallback für alte Geräte.
            Im Gegensatz zum Autosave (der auf watch-Callbacks angewiesen ist,
            die auf langsamen Geräten nie feuern können) liest saveNow() die
            aktuellen Formularwerte via form.getValues() und sendet sie sofort.
            Der Button ist immer klickbar — isDirty wird NICHT als disabled-Kriterium
            verwendet, da isDirty auf dem watch-Callback-Tracking basiert, das auf
            alten Geräten unzuverlässig sein kann. */}
        <div className="pt-2">
          <button
            type="button"
            data-testid="manual-save-button"
            onClick={() => triggerManualSave()}
            disabled={saveStatus === 'saving'}
            className={clsx(
              'btn btn-sm',
              saveStatus === 'saving'
                ? 'btn-disabled'
                : 'btn-outline btn-primary',
            )}
          >
            {saveStatus === 'saving' ? (
              <>
                <span className="loading loading-spinner loading-xs"></span>
                {t('autosave_saving', 'Speichern...')}
              </>
            ) : (
              t('autosave_save', 'Speichern')
            )}
          </button>
        </div>
      </form>
    </WrapperWidget>
  );
}

export function PropertyConditionsQualityFormWidget(
  props: Props & { readonly hideInV3?: boolean },
) {
  const v3 = useV3Redesign();
  // PropertyFormWidgetV3 already owns these fields and the form registration; mounting both would overwrite submitFnRef.
  return v3 && props.hideInV3 ? (
    <></>
  ) : (
    <LegacyPropertyConditionsQualityFormWidget {...props} />
  );
}
